import React, { useEffect } from 'react';

import { useDispatch } from 'react-redux';
import cn from 'class-names';

import WorkspaceSelect from '@components/WorkspaceSelect/WorkspaceSelect';
import ResponaNewLogo from '@uikit/old/ResponaNewLogo/ResponaNewLogo';

import { checkIsAnyPending } from '@utils/loadingChecks';
import { fetchAppBillingCredits } from '@redux/thunks/billings-app.thunks';
import { isAppRealm, isConciergeRealm, isPublisherRealm } from '@utils/realmIdentifier';
import { fetchConciergeBillingDetails } from '@redux/thunks/billings-concierge.thunks';
import { DispatchType } from 'src/store';

import { useActiveBilling, useBillingLoadingStatus } from '@hooks/useActiveBilling';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';
import SidebarPagesLinks from './components/SidebarPagesLinks';
import SidebarFooter from './components/SidebarFooter/SidebarFooter';

import { pagesLinks } from './pagesLinks';

import './Sidebar.scss';

function SideBar({ pathname, preview }: { pathname: string; preview?: boolean }): JSX.Element {
  const dispatch = useDispatch<DispatchType>();

  const currentWorkspaceId = useCurrentWorkspaceId();
  const isPaymentStatusActive = useActiveBilling();
  const billingLs = useBillingLoadingStatus();

  useEffect(() => {
    if (checkIsAnyPending(billingLs) && !preview) {
      if (isConciergeRealm()) {
        dispatch(fetchConciergeBillingDetails());
      } else if (isAppRealm()) {
        dispatch(fetchAppBillingCredits());
      }
    }
  }, [billingLs, preview, dispatch]);

  const cnSidebar = cn('sidebar', {
    'sidebar--closed': true,
    'sidebar--hovered': false,
  });
  const cnSidebarPages = cn('sidebar__pages', {
    'sidebar__pages--disabled': !isPaymentStatusActive && !isPublisherRealm(),
  });
  const cnSidebarWorkspace = cn('sidebar__workspace', {
    'sidebar__workspace--disabled': !isPaymentStatusActive && !isPublisherRealm(),
  });

  return (
    <div className={cnSidebar} tabIndex={0} role="button" data-testid="sidebar">
      <div className="sidebar__header">
        <span className="sidebar__logo sidebar__logo--respona">
          <ResponaNewLogo size="small" />
        </span>
      </div>
      <div className={cnSidebarWorkspace}>
        <WorkspaceSelect preview={preview} />
      </div>
      <div className={cnSidebarPages} onClick={(e) => e.stopPropagation()}>
        <SidebarPagesLinks pathname={pathname} pages={pagesLinks(currentWorkspaceId)} />
      </div>
      {!preview && <SidebarFooter />}
    </div>
  );
}

export default SideBar;
